<template>
  <modal-branch-change ref="modal-branch-change" @update-rebase-number="sendNumberOfRebaseChange" @reload="change++" />
  <div>
    <div class="page-content vh-100 d-flex align-items-center justify-content-center" v-if="isLoading">
      <div class="container-fluid">
        <div class="d-flex justify-content-center">
          <b-spinner variant="dms-primary" />
        </div>
      </div>
    </div>
    <nav-tab v-if="$route.meta.showBreadcrumb === true">
      <div :key="change">
        <iframe class="iframe-integration" :src="url" frameborder="0" @load="onIframeLoad" ref="iframe"></iframe>
      </div>
    </nav-tab>
    <div :key="change" v-else>
      <iframe class="iframe-integration" :src="url" frameborder="0" @load="onIframeLoad" ref="iframe"></iframe>
    </div>
  </div>
</template>
<script>
import { mapMutations, mapState } from 'vuex';
import { getPlatformHostname } from '@version/models/headers-handler';
import { authenticationMixin } from '@common/security/authentication-mixin';
import NavTab from '@components/layouts/nav-tab';
import ModalBranchChange from '@components/containers/modal-branch-change.vue';

export default {
  mixins: [authenticationMixin],
  components: { NavTab, ModalBranchChange },
  props: {
    locale: {
      type: String,
    },
    siteId: {
      type: Number,
    },
  },
  data() {
    return {
      change: 0,
      isLoading: true,
      lastPathname: '',
      url: '',
      openModalListBranch: false,
    };
  },
  mounted() {
    if (this.$route.query?.branchModalOpen) {
      this.openModalListBranch = true;
    }
    this.messageListener = async event => {
      const { type } = event.data;
      let canonicPath;
      switch (type) {
        case 'CHECK_EXTENSION':
          const iframe = this.$refs.iframe.contentWindow;
          const extensionId = event.data?.extensionId;
          const extensionTestUrl = `chrome-extension://${extensionId}/icons/48.png`;

          const checkExtensionAvailable = async extensionUrl => {
            try {
              const response = await fetch(extensionUrl, { method: 'HEAD' });
              return response.ok;
            } catch {
              return false;
            }
          };

          const extensionAvailable = await checkExtensionAvailable(extensionTestUrl);
          const type = extensionAvailable ? 'EXTENSION_AVAILABLE' : 'EXTENSION_UNAVAILABLE';
          iframe.postMessage({ type: type, origin: window.location.origin }, '*');
          break;
        case 'refreshLocation':
          canonicPath = this.trimSlashes(event.data.pathname);
          if (this.lastPathname !== canonicPath) {
            this.lastPathname = canonicPath;
            this.$router.replace({ query: { p: this.lastPathname } });
          }
          this.handleIframeAndStoreContainerIDDifferences(canonicPath);
          this.consentManagementNavigationDetect(canonicPath);
          break;
        case 'timeout':
          this.$store.dispatch('currentUser/timeout');
          break;
        case 'hideSidebar':
          this.hideSidebar();
          break;
        case 'newUserAdded':
          this.$store.dispatch('currentUser/loadUser');
          break;
        case 'pushNewRoute':
          this.$router.push(event.data.route);
          break;
        case 'openModalRecapBranch':
          this.$refs['modal-branch-change'].openModal('diff', event.data?.containerID);
          break;
        case 'openModalMerge':
          this.$refs['modal-branch-change'].openModal('merge');
          break;
        case 'openModalRebaseAlert':
          this.$refs['modal-branch-change'].openModal('rebaseAlert');
          break;
        case 'openModalRebase':
          this.$refs['modal-branch-change'].openModal('rebase');
          break;
        case 'sendMergeRequest':
          this.$refs['modal-branch-change'].openModal('sendMergeRequest');
          break;
        case 'getNumberOfRebaseChange':
          this.sendNumberOfRebaseChange();
          break;
        case 'change-branch':
          const containerId = event.data?.containerID;
          this.$store.dispatch('tms/webContainer/loadCurrent', containerId);
          this.$store.commit('tms/webTag/_clearEntities');
          await this.$store.dispatch('tms/webTag/findAll', {
            include: 'template,template.editor',
            filter: { container: containerId },
          });
          const firstTag = this.$store.getters['tms/webTag/getFirstActiveOrAvailableTag'](containerId);
          this.$router.push({
            params: { containerID: containerId, tagID: firstTag?.id },
          });
          break;
        case 'tmsCurrentTagChanged':
          this.$router.push({ params: { tagID: event.data?.id } });
          break;
        case 'tmsCurrentContainerChanged':
          this.$router.push(event.data.route).then(() => {
            const containerID = event.data.route.params.containerID;
            this.$store.commit(
              'tms/webContainer/_setCurrentEntity',
              this.$store.state.tms.webContainer.keyedById[containerID]
            );
          });
          break;
        case 'openModalListBranch':
          if (this.openModalListBranch) {
            this.openModalListBranch = false;
            this.openCreateBranchModal();
          }
          break;
        case 'redirect:error:403':
          this.$router.push({
            name: 'page-error',
            query: {
              askedPage: event.data.message === 'ip-not-allowed' ? 'ip-not-allowed' : event.data.routeName,
              status_code: event.data.status_code || undefined,
              title: event.data.title || undefined,
            },
          });
          break;
        case 'pushNotification':
          this.$store.commit(`notifications/${event.data.notificationType}`, {
            text: event.data.notificationMessage,
            timeout: true,
          });
          break;
      }
    };

    window.addEventListener('message', this.messageListener);

    window.addEventListener('message', event => {
      const isSameOrigin =
        event.origin === getPlatformHostname() || `${event.origin}/v5/web/app_dev.php` === getPlatformHostname();

      if (event.data === 'activity-on-iframe' && isSameOrigin) {
        this.checkPlatformSession();
      }
    });

    this.isLoading = true;

    if (this.$route.query.p) {
      this.lastPathname = this.$route.query.p;
      this.computeUrl(this.$route.query.p);
    } else {
      this.computeUrl();
    }
  },
  watch: {
    $route(to, from) {
      if (to.path !== from.path) {
        this.computeUrl();
        return;
      }
      if (to.query.p !== this.lastPathname && to.query.p) {
        this.lastPathname = to.query.p;
        this.computeUrl(to.query.p);
      }
    },
    // reload the iframe when the user session changes so that it get the new csrf token
    hasSession(val) {
      if (val) {
        this.change++;
      }
    },
  },
  computed: {
    ...mapState('currentUser', ['hasSession']),
  },
  methods: {
    ...mapMutations('layout', ['hideSidebar']),

    // handle case where a branch is created
    handleIframeAndStoreContainerIDDifferences(newUrl) {
      const regex = /\/containers\/(?:generate|edit)\/(\d+)\//;
      const match = newUrl.match(regex);
      // If the URL has a different container ID than the current one, load the new container
      if (match?.length > 1 && this.$store.state.tms.webContainer?.entity?.id != match[1]) {
        const containerID = match[1];
        this.$store.dispatch('tms/webContainer/loadCurrent', containerID);
        this.$router.push({ params: { containerID }, query: { p: newUrl } });
      }
    },
    consentManagementNavigationDetect(newUrl) {
      if (newUrl.includes('containers/privacy/tags')) {
        this.$router.push({ name: 'privacy-banners-tag-assignation' });
      }
      if (newUrl.includes('containers/privacy/categories')) {
        this.$router.push({ name: 'privacy-banners-consent-categories' });
      }
      if (newUrl.includes('containers/privacy/vendors')) {
        this.$router.push({ name: 'privacy-banners-vendor-settings' });
      }
    },
    sendNumberOfRebaseChange() {
      this.$refs.iframe.contentWindow.postMessage(
        {
          type: 'numberOfRebaseChange',
          numberOfChanges: this.$refs['modal-branch-change'].numberOfUnhandledChangesToRebase,
        },
        '*'
      );
    },
    openCreateBranchModal() {
      this.$refs.iframe.contentWindow.postMessage(
        {
          type: 'openCreateBranchModal',
          origin: window.location.origin,
        },
        new URL(getPlatformHostname()).origin
      );
    },
    async computeUrl(path) {
      this.isLoading = true;
      const prefix = `${this.$route.params.locale}/${this.$route.params.siteId}`;
      let queryParams = `?redirect_prefix=${prefix}&redirect_route_name=${this.$route.name}`;
      if (!path) {
        let symfonyPath = null;
        if (typeof this.$route.meta.symfonyPath === 'function') {
          symfonyPath = await Promise.resolve(this.$route.meta.symfonyPath(this.$route));
        } else {
          symfonyPath = this.$route.meta?.symfonyPath;
        }
        path = symfonyPath ? `/${prefix}${symfonyPath}` : `${this.$route.fullPath}`;
      }
      if (this.$route.query) {
        const searchParams = new URLSearchParams(this.$route.query);
        queryParams = searchParams.size ? `${queryParams}&${searchParams.toString()}` : queryParams;
      }
      path = `/${path}`;
      this.url = `${getPlatformHostname()}${this.trimSlashes(path)}${queryParams}`;
      this.change++;
    },
    onIframeLoad() {
      this.isLoading = false;
      this.postInit();
    },
    postInit() {
      if (!this.$refs.iframe?.contentWindow) return;
      const iframe = this.$refs.iframe.contentWindow;
      const query = {
        type: 'init',
        origin: window.location.origin,
      };

      iframe.postMessage(query, new URL(getPlatformHostname()).origin);
      iframe.postMessage({ type: 'iframe_ready' }, '*');
    },
    trimSlashes(url) {
      return url.replace(/\/+/g, '/');
    },
  },
  unmounted() {
    window.removeEventListener('message', this.messageListener);
  },
};
</script>

<style lang="scss" scoped>
.iframe-integration {
  padding-top: 10px;
  height: calc(100vh - 6px);
  overflow: hidden;
  width: 100%;
}

.tmp-button {
  position: absolute;
  top: 70px;
}
</style>
